<!--
  移动端通用可编辑大表格组件（基于 Handsontable，Vue2 版本）

  与 if-web 的 src/components/Formily/HotTable.vue 同源：内核、数据绑定模式、
  label/value 适配、校验规则（./hotTableValidation）全部保持一致，因此同一份
  表单设计器产出的 schema 可以直接在移动端渲染，无需为移动端单独配一套。

  差异集中在「交互适配」，桌面端那套鼠标交互在手机上基本不可用：
  ┌──────────────┬────────────────────────┬──────────────────────────────────┐
  │ 能力         │ 桌面端做法             │ 移动端替代方案                   │
  ├──────────────┼────────────────────────┼──────────────────────────────────┤
  │ 行增删       │ 右键菜单 contextMenu   │ 长按单元格 → Vant ActionSheet    │
  │ 进入编辑     │ 双击 / Enter           │ 轻点后延时编辑（tapToEdit）      │
  │ 批量填充     │ 拖拽右下角填充手柄     │ 关闭（触摸下会与滚动抢手势）     │
  │ 调列宽/移列  │ 拖拽表头               │ 关闭（同上）                     │
  │ 列筛选       │ 表头筛选浮层           │ 关闭（浮层挂 body，窄屏也放不下）│
  │ 宽表浏览     │ 屏幕够宽直接看         │ 冻结首列 + 横向滚动 + 横屏全屏   │
  └──────────────┴────────────────────────┴──────────────────────────────────┘

  横屏（enableLandscape）：
  - 点击工具条「横屏编辑」进入全屏，优先走原生 requestFullscreen +
    screen.orientation.lock('landscape')（Android / X5 多数支持，系统级真旋转）；
  - iOS WebView 不支持 orientation.lock，此时只进全屏并提示用户手动转屏。
    这里**不用** CSS rotate(90deg) 兜底 —— 真机实测系统键盘不会跟着转（键盘竖、
    表格横，没法打字），日期编辑器又被内核挂到 document.body 上同样不跟随旋转，
    CSS 层面无解。详见 enterFullscreen 的注释。
  - 方向 / 全屏切换后必须重新测量表格尺寸，见 refreshDimensions。
  - 横屏后列少的表格会自动按比例拉伸占满宽度（stretchH 内核语义 'all'），
    列多的表格不拉伸、保持横向滚动 —— 详见 effectiveStretchH 的注释；
    不想要该行为的调用方传 stretchAutoFullscreen: false。
  - 横屏后页面整体放大/变形的根因与修复见 ./hotTableRem.js（组件里也
    import 了它） —— amfe-flexible 按 clientWidth/10 设根字号，竖转横后
    clientWidth 从 375 变 812，根字号翻倍，所有 rem 布局被放大 2.17 倍。

  轻点进入编辑（tapToEdit）：
  - 内核在移动端把 touchstart/touchend 合成为 mousedown/mouseup（见
    vendor 的 walkontable/event.js onTouchEnd -> onMouseUp），原实现挂在
    afterOnCellMouseUp 上，表现为"手指刚碰到单元格就弹出输入框"，用户
    想滚动表格时根本来不及反应。
  - 现在的交互：touchstart 记录起点与时间，touchend 时判定为"轻点"（位移
    < 10px、时长 < 500ms、期间无长按面板触发）才启动编辑，且再延迟
    tapEditDelay（默认 1000ms）唤起输入框 —— 用户按下后有一整秒可以开始
    拖动表格，一旦移动超过阈值就取消编辑。
  - 长按（>= 500ms）依旧走行操作面板，不受影响。

  行的新增与删除（与桌面端的关键差异）：
  - minRows 与 minSpareRows 默认都是 0，即内核绝不自动补空行。
    桌面端 minRows 为 1，但在移动端会造成两个体感 bug：编辑时凭空多一行、
    删除最后一行后立刻被补回来（用户认为删除按钮坏了）。
  - 新增完全由用户主动触发：底部「添加一行」按钮，或长按单元格的插入/复制。

  关于 postcss-pxtorem（重要）：
  本项目把所有 CSS 的 px 转成 rem，而 handsontable 的冻结行列 overlay 依赖
  JS 实测 px 对齐，rem 带来的小数误差会让表头与数据列错位。因此
  vendor/handsontable 已在 .postcssrc.js 里被 exclude；表格自身的尺寸
  （行高 / 列宽）一律通过 rowHeights / colWidths 等原生配置由 JS 下发，
  绝不用 CSS 覆盖 td 的 height / padding。
-->
<template>
  <div
    class="hot-table-mobile"
    :class="{ 'is-fullscreen': isFullscreen }"
  >
    <!-- 全屏态顶栏：提供明确的退出入口，避免用户被困在全屏里 -->
    <div v-if="isFullscreen" class="htm-topbar">
      <span class="htm-topbar-title">{{ fullscreenTitle }}</span>
      <button type="button" class="htm-topbar-btn" @click="exitFullscreen">
        退出全屏
      </button>
    </div>

    <!--
      方向锁不可用（iOS WebView 等）时引导用户手动转屏。
      这里只提示、不做 CSS 旋转，原因见 enterFullscreen 的注释。
    -->
    <div v-if="showRotateHint" class="htm-rotate-hint">
      请将手机转为横屏，以获得更宽的编辑区域
    </div>

    <div
      ref="hotContainer"
      class="htm-host"
      :style="hostStyle"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
      @touchcancel="handleTouchEnd"
    ></div>

    <div v-if="showFooter" class="htm-toolbar">
      <button
        v-if="showRowToolbar"
        type="button"
        class="htm-btn htm-btn-primary"
        :disabled="addRowDisabled"
        @click="addRow()"
      >
        + 添加一行
      </button>
      <button
        v-if="enableLandscape && !isFullscreen"
        type="button"
        class="htm-btn"
        @click="enterFullscreen"
      >
        横屏编辑
      </button>
      <span v-if="errorSummary" class="htm-error-tip" :title="errorSummary">
        {{ errorSummary }}
      </span>
      <span v-else-if="hintText" class="htm-tip">{{ hintText }}</span>
    </div>

    <!-- 行操作面板：替代桌面端的右键菜单，由长按单元格唤起 -->
    <van-action-sheet
      v-model="rowSheetVisible"
      :actions="rowSheetActions"
      :description="rowSheetDescription"
      get-container="body"
      cancel-text="取消"
      close-on-click-action
      @select="handleRowAction"
    />
  </div>
</template>

<script>
import Handsontable from 'handsontable';
// 副作用导入：自注册 zh-CN 语言包，之后在 settings 里用 language: 'zh-CN' 即可生效
import 'handsontable/languages/zh-CN';
import 'handsontable/dist/handsontable.full.css';
// 移动端补充样式：禁用长按弹出系统菜单 / 文本选中，开启原生惯性滚动
import 'handsontable/dist/mobile.handsontable.css';
import { ActionSheet, Toast } from 'vant';
import {
  formatValidationMessage,
  validateCellValue,
  validateTableData,
} from './hotTableValidation';
// 横屏 rem 修正：amfe-flexible 在横屏时按 clientWidth/10 放大根字号，
// 页面整体被拉伸变形（用户反馈的"横屏变形"即此），mounted 时安装钳制补丁。
// 详见 ./hotTableRem.js 头注释。
import { installLandscapeRemFix } from './hotTableRem';

// handsontable 已内置为项目源码（vendor/handsontable，MIT v6.2.2 基线），由
// webpack alias 指向，不依赖 npm 包，与 if-web 是同一份内核。
// 该基线在 index.js 中已静态注册全部 cellType/editor/renderer/plugin，
// 因此无需再调用 v9+ 才引入的 registerAllModules()。

/**
 * 移动端统一行高（px）。
 *
 * 桌面端取 30px；移动端提到 40px —— iOS 人机界面指南建议可点区域不小于 44pt，
 * 40px 行高配合单元格内边距后实际可点高度约 44px，是"能稳定点中"与
 * "一屏能看到足够行数"之间的折中。
 */
const MOBILE_ROW_HEIGHT = 40;

/** 移动端列最小宽度（px）：窄于此值文字会被挤成竖排，无法阅读 */
const MIN_COL_WIDTH = 96;

/** 长按判定时长（ms）：短于 400ms 容易与轻点误判，长于 600ms 用户会以为没反应 */
const LONG_PRESS_MS = 500;

/** 长按期间允许的手指位移（px）：超过即认为用户在滚动表格，取消长按 */
const LONG_PRESS_MOVE_TOLERANCE = 8;

/**
 * 轻点判定允许的手指位移（px）：超过说明用户在拖动表格，不进编辑。
 * 10px 在"稳稳点中单元格"与"误判滚动"之间取的折中。
 */
const TAP_MOVE_TOLERANCE = 10;

/**
 * 轻点判定时长上限（ms）：按压超过该值视为"按住"，不进编辑。
 * 与长按阈值 LONG_PRESS_MS（500）对齐：按压到 500ms 会先弹出行操作面板
 * （longPressFired 挡住本钩子），因此 300~500ms 的短暂按住仍按轻点处理、
 * 延时唤起编辑器，避免出现"既没面板也不进编辑"的死区。
 */
const TAP_MAX_DURATION_MS = LONG_PRESS_MS;

/**
 * 轻点命中后延迟多久唤起输入框（ms）。
 * 用户诉求："点击一秒后再唤起输入框"。在这一秒里用户可以自由拖动表格，
 * 一旦 touchmove 超过位移阈值就把这次按压判为拖动、取消编辑。
 */
const TAP_EDIT_DELAY_MS = 1000;

/**
 * 触摸结束后多久内的 mouseup/click 视为浏览器补发的合成事件（ms）。
 * iPad 桌面版 UA 等场景下内核会同时注册触摸与鼠标监听，touchend 后浏览器
 * 仍会补发 mouseup；600ms 足以覆盖所有浏览器的合成事件补发窗口。
 */
const SYNTHETIC_MOUSE_EVENT_WINDOW_MS = 600;

// 对象数组模式下，若未显式传 dataSchema，则按 columns[].data（字符串字段名，
// 支持 'a.b' 形式的嵌套路径）自动推断一份数据结构模板，供 handsontable 在
// value 为空数组、或新增行时构造出结构正确的新对象行。
// 若 columns 里没有任何字符串 data 键（纯矩阵模式），返回 undefined 即可。
function buildDataSchemaFromColumns(columns) {
  if (!Array.isArray(columns) || !columns.length) return undefined;
  const hasObjectKey = columns.some(
    (c) => c && typeof c.data === 'string' && !/^\d+$/.test(c.data),
  );
  if (!hasObjectKey) return undefined;
  const schema = {};
  columns.forEach((c) => {
    if (!c || typeof c.data !== 'string') return;
    const path = c.data.split('.');
    let cursor = schema;
    path.forEach((key, idx) => {
      if (idx === path.length - 1) {
        cursor[key] = null;
      } else {
        cursor[key] = cursor[key] || {};
        cursor = cursor[key];
      }
    });
  });
  return schema;
}

// ============ 下拉类列的 label/value 支持 ============
// handsontable 原生 dropdown/autocomplete/multiselect 的 source 只接受一维原始值数组，
// 不支持 [{ label, value }] 这种对象数组（会渲染成 [object Object]，且 strict 校验必然失败）。
// 这里做一层适配：识别到对象数组形式的 source 时，
//   1) source 换成 label 数组 —— 用户在下拉里看到的是 label
//   2) 注入 renderer —— 单元格按存储的 value 反查显示 label
//   3) 注入 validator —— 放行 value（原生校验器只认 source 里的 label，会误判）
//   4) 配合 beforeChange 钩子把用户选中的 label 转换成 value 落库
// 字符串数组写法完全不受影响，保持原生行为。
const OPTION_CELL_TYPES = ['dropdown', 'autocomplete', 'multiselect'];

function isLabelValueSource(source) {
  return (
    Array.isArray(source)
    && source.length > 0
    && source.every((it) => it && typeof it === 'object' && !Array.isArray(it) && 'value' in it)
  );
}

// dropdown 默认渲染器会画下拉箭头，这里复用它、只替换显示文本，保证外观一致
function createLabelRenderer(valueToLabel) {
  return function labelRenderer(instance, td, row, col, prop, value, cellProperties) {
    const key = value === null || value === undefined ? '' : String(value);
    const display = valueToLabel.has(key) ? valueToLabel.get(key) : value;
    const baseRenderer = Handsontable.renderers.getRenderer(
      cellProperties.type === 'multiselect' ? 'text' : 'autocomplete',
    );
    baseRenderer.call(this, instance, td, row, col, prop, display, cellProperties);
  };
}

/**
 * 把内联 CSS 应用到单元格的渲染器包装。
 *
 * 列配置里的"单元格样式"支持直接写 CSS（如 color: red），而 handsontable 的
 * className 只接类名、内联样式必须在 renderer 里写 td。这里保留并调用原渲染器，
 * 仅在其后追加 cssText，因此下拉箭头、label/value 反查等既有渲染行为不受影响。
 *
 * @param {Function} baseRenderer 原渲染器；为空时按 cellType 回退到内置渲染器
 * @param {String} style `k:v;` 形式的 CSS 声明
 */
function wrapRendererWithStyle(baseRenderer, style) {
  return function styledRenderer(instance, td, row, col, prop, value, cellProperties) {
    const renderer = baseRenderer
      || Handsontable.renderers.getRenderer(cellProperties.type || 'text');

    renderer.call(this, instance, td, row, col, prop, value, cellProperties);

    // 追加而非覆盖：保留渲染器自身设置的样式
    td.style.cssText += style;
  };
}

/**
 * 单元格校验器：必填 + 选项范围 + 数字 / 日期格式，规则统一来自
 * hotTableValidation，与提交时的表单级校验完全一致。
 *
 * 注意校验只负责"标红"，不阻止用户把值输进去（否则无法边填边改）；
 * 真正的提交拦截在 HotTable.ts 的 Formily 字段校验器里完成。
 *
 * @param {Object} column 原始列配置（未做 label/value 归一化，含 required 与原始 source）
 */
function createCellValidator(column) {
  return function cellValidator(value, callback) {
    // 移动端不做任何空行豁免：minRows 恒为 0，表里的空行必然来自用户
    // 主动新增（打算填但还没填），必填列为空照样标红提示"这一行要填"。
    // （PC 端 if-web 因 minRows=1 存在内核自动补的初始行才做"全表唯一空行"豁免。）
    callback(!validateCellValue(value, column, {}));
  };
}

/** 对齐方式 -> handsontable 内置类名 */
const ALIGN_CLASS_MAP = {
  left: 'htLeft',
  center: 'htCenter',
  right: 'htRight',
};

/**
 * 给列补上默认对齐类名，统一整表观感。
 *
 * numeric 渲染器会给数字列自动追加 htRight（见内核 numericRenderer），
 * 造成"文本列左对齐、数字列右对齐"的不一致。这里显式写入对齐类名，
 * 而 numericRenderer 只在"没有任何对齐类名"时才追加 htRight，
 * 因此写入后它就不会再改动。
 *
 * @param {Object} col 列配置
 * @param {String} align 表格级默认对齐
 */
function withDefaultAlign(col, align) {
  const alignClass = ALIGN_CLASS_MAP[align];
  if (!alignClass) return col;

  const existing = typeof col.className === 'string' ? col.className : '';

  // 列级已指定对齐，或用 CSS 写了 text-align，都不覆盖
  if (/\bht(Left|Center|Right|Justify)\b/.test(existing)) return col;
  if (typeof col.__cellStyle === 'string' && /text-align\s*:/.test(col.__cellStyle)) return col;

  return {
    ...col,
    className: existing ? `${existing} ${alignClass}` : alignClass,
  };
}

/**
 * 归一化 columns，返回 { columns, labelValueMaps }。
 * labelValueMaps 以列下标为键，供 beforeChange 做 label -> value 转换。
 *
 * @param {Array} columns 原始列配置
 * @param {String} align 表格级默认对齐
 */
function normalizeColumns(columns, align) {
  const labelValueMaps = {};
  if (!Array.isArray(columns) || !columns.length) {
    return { columns: undefined, labelValueMaps };
  }
  const normalized = columns.map((rawCol, index) => {
    if (!rawCol || typeof rawCol !== 'object') return rawCol;

    const col = withDefaultAlign(rawCol, align);
    const isOptionType = OPTION_CELL_TYPES.includes(col.type);

    // 非 label/value 形态的列同样需要校验器（必填、数字、日期、字符串数组选项）
    if (!isOptionType || !isLabelValueSource(col.source)) {
      const plain = { ...col, validator: col.validator || createCellValidator(col) };
      // 列配置里写了 CSS 声明时，包装渲染器把样式落到 td
      if (col.__cellStyle) {
        plain.renderer = wrapRendererWithStyle(col.renderer, col.__cellStyle);
      }
      return plain;
    }

    const valueToLabel = new Map();
    const labelToValue = new Map();
    const labels = [];
    col.source.forEach((item) => {
      const value = item.value === null || item.value === undefined ? '' : String(item.value);
      // label 缺省时回退用 value 显示，避免出现空白选项
      const label = item.label === null || item.label === undefined ? value : String(item.label);
      valueToLabel.set(value, label);
      labelToValue.set(label, value);
      labels.push(label);
    });

    labelValueMaps[index] = { labelToValue, valueToLabel };

    // 允许业务方自定义 renderer/validator 覆盖默认适配行为
    let renderer = col.renderer || createLabelRenderer(valueToLabel);

    // 样式包装放在最后：内部会调用上面的 labelRenderer，
    // 因此 label 反查与下拉箭头都保留，只是额外追加了内联 CSS
    if (col.__cellStyle) {
      renderer = wrapRendererWithStyle(renderer, col.__cellStyle);
    }

    return {
      ...col,
      source: labels,
      renderer,
      // 校验用的是原始列配置：label 与 value 两种形态都视为合法
      validator: col.validator || createCellValidator(col),
    };
  });
  return { columns: normalized, labelValueMaps };
}

export default {
  // 注意：这里直接使用 handsontable 核心库而非 @handsontable/vue 封装。
  // 原因是本组件 name 为 'HotTable'，若再局部注册同名的封装组件，
  // Vue2 的 Vue.extend 会执行 `Sub.options.components[name] = Sub`（用于递归自引用），
  // 把同名的局部注册覆盖成组件自身，导致模板里的 <HotTable> 解析成自己而无限递归，
  // 抛出 RangeError: Maximum call stack size exceeded。
  name: 'HotTable',
  components: {
    [ActionSheet.name]: ActionSheet,
  },
  props: {
    // 表格数据：支持二维数组（矩阵模式）或对象数组（外部接口返回的 [{name,age}...]，
    // 需配合 columns[].data 为字符串字段名使用），与 columns 的 data 字段配合使用
    value: {
      type: Array,
      default: () => [],
    },
    // 列定义，参考 handsontable columns 配置；为空时由 handsontable 按数据自动推断。
    // 除原生字段外额外支持 required: true（必填校验）
    columns: {
      type: Array,
      default: () => [],
    },
    // 对象数组模式下的新行数据结构模板；不传时会根据 columns[].data 自动推断
    dataSchema: {
      type: [Object, Array],
      default: undefined,
    },
    colHeaders: {
      type: [Boolean, Array],
      default: true,
    },
    rowHeaders: {
      type: [Boolean, Array],
      default: true,
    },
    // 表格高度（固定高度，不随行数自适应）。
    // 移动端默认 320px：一屏放得下约 7 行，再高会把页面上其它表单项挤出视口。
    // 全屏态下该值被忽略，表格自动撑满可用高度。
    height: {
      type: [String, Number],
      default: 320,
    },
    /** 单元格默认水平对齐：'left' | 'center' | 'right' */
    align: {
      type: String,
      default: 'left',
      validator: (value) => ['left', 'center', 'right'].indexOf(value) > -1,
    },
    /** 行高（px）。移动端默认 40，保证手指能稳定点中 */
    rowHeight: {
      type: Number,
      default: MOBILE_ROW_HEIGHT,
    },
    /**
     * 列最小宽度（px）。移动端不做整表拉伸，靠横向滚动看宽表，
     * 因此需要给每列一个可读的下限，避免文字被挤成竖排。
     */
    minColWidth: {
      type: Number,
      default: MIN_COL_WIDTH,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    /**
     * 自动预留的空白行数，对应 handsontable 原生 minSpareRows。
     *
     * 必须保持 0：非 0 时内核会在「用户填完最后一行」后自动追加空行
     * （见 core.js 的 grid.adjustRowsAndCols），移动端表现为编辑着就凭空多一行，
     * 用户以为自己点错了。行的新增一律由用户主动触发。
     */
    minSpareRows: {
      type: Number,
      default: 0,
    },
    /**
     * 最少行数，对应 handsontable 原生 minRows。
     *
     * 必须保持 0（与桌面端的 1 不同）：minRows >= 1 时内核会在行数不足时
     * 自动补空行，导致「删除最后一行」删完立刻被补回来，用户认为删除失效。
     * 数据为空时表格显示 0 行，由底部「添加一行」按钮引导录入。
     */
    minRows: {
      type: Number,
      default: 0,
    },
    minCols: {
      type: Number,
      default: 0,
    },
    maxRows: {
      type: Number,
      default: 200,
    },
    maxCols: {
      type: Number,
      default: 200,
    },
    contextMenu: {
      type: [Boolean, Object],
      default: false,
    },
    allowInsertRow: {
      type: Boolean,
      default: true,
    },
    allowRemoveRow: {
      type: Boolean,
      default: true,
    },
    showAddRowButton: {
      type: Boolean,
      default: true,
    },
    allowInsertColumn: {
      type: Boolean,
      default: false,
    },
    allowRemoveColumn: {
      type: Boolean,
      default: false,
    },
    showErrorSummary: {
      type: Boolean,
      default: true,
    },
    filters: {
      type: Boolean,
      default: false,
    },
    columnSorting: {
      type: Boolean,
      default: false,
    },
    manualColumnResize: {
      type: Boolean,
      default: false,
    },
    manualRowResize: {
      type: Boolean,
      default: false,
    },
    manualColumnMove: {
      type: Boolean,
      default: false,
    },
    manualRowMove: {
      type: Boolean,
      default: false,
    },
    fixedColumnsLeft: {
      type: Number,
      default: 0,
    },
    fixedRowsTop: {
      type: Number,
      default: 0,
    },
    fillHandle: {
      type: [Boolean, String],
      default: false,
    },
    mergeCells: {
      type: [Boolean, Array],
      default: false,
    },
    comments: {
      type: Boolean,
      default: false,
    },
    /**
     * 列宽拉伸策略，对应 handsontable 原生 stretchH：'none' | 'last' | 'all'。
     *
     * 移动端必须保持 'none'，否则宽表无法左右滑动。原因在内核里很明确：
     *   1) viewport.js 的 getWorkspaceWidth()：stretchH 不为 'none' 时直接返回
     *      「容器宽度」；为 'none' 时返回 max(容器宽, 表格实际宽度)，
     *      后者才会让 wtHolder 产生横向滚动条；
     *   2) calculator/viewportColumns.js 的 refreshStretching()：'last' 会把
     *      remainingSize（容器宽 - 各列宽之和，宽表下为负数）加到最后一列上，
     *      把整表宽度硬压回容器宽度。
     * 两者叠加的结果就是「列被压扁且滑不动」。
     */
    stretchH: {
      type: String,
      default: 'none',
    },

    // ================= 移动端专属 =================
    /**
     * 触摸优化总开关（默认开启）。
     *
     * 开启后会强制关掉一批"必须靠鼠标拖拽"的能力，即使 schema 里显式传了 true：
     * 填充手柄、拖拽调列宽/行高、拖拽移动列/行、右键菜单、列筛选。
     * 这些功能在触摸屏上会与页面滚动抢手势，实测表现为"想滚动结果改了列宽"。
     *
     * 之所以做成"强制覆盖"而不只是改默认值：同一份表单 schema 要同时供
     * PC 端（if-web）和移动端渲染，PC 端那边这些开关通常都是 true。
     *
     * 平板等大屏触摸设备若想恢复完整能力，把它关掉即可。
     */
    touchOptimized: {
      type: Boolean,
      default: true,
    },
    /**
     * 冻结首列。宽表横向滚动时保留第一列（通常是资产编号/名称这类标识列），
     * 否则滑到右边就不知道自己在编辑哪一行了。
     * 仅在 fixedColumnsLeft 为 0（未显式指定冻结列数）时生效。
     */
    freezeFirstColumn: {
      type: Boolean,
      default: true,
    },
    /** 单击单元格直接进入编辑（移动端没有"双击"的肌肉记忆） */
    tapToEdit: {
      type: Boolean,
      default: true,
    },
    /**
     * 轻点单元格后，延迟多少毫秒唤起输入框。
     * 默认 1000ms：按下后有一整秒可以开始拖动表格，一旦拖动（位移超过
     * 10px）就取消唤起。需求来自"想滚动却被立即弹出的输入框挡住"的反馈。
     * 设为 0 则轻点后立即唤起（恢复旧版单击即编辑的手感）。
     */
    tapEditDelay: {
      type: [Number, String],
      default: TAP_EDIT_DELAY_MS,
    },
    /** 长按单元格唤起行操作面板（替代桌面端右键菜单） */
    longPressRowMenu: {
      type: Boolean,
      default: true,
    },
    /** 是否提供"横屏编辑"入口 */
    enableLandscape: {
      type: Boolean,
      default: true,
    },
    /**
     * 全屏（横屏编辑）时是否自动把 stretchH 提升为 'all'（默认开启）。
     * 开启时：横屏后列少的表格会按比例拉伸占满宽度；列多的表格不受影响
     * （内核在剩余空间不足时不拉伸，宽表依旧横向滚动）。
     * 不想拉伸的调用方显式传 false，横屏后保持原始列宽 + 横向滚动。
     */
    stretchAutoFullscreen: {
      type: Boolean,
      default: true,
    },
    /** 全屏态顶栏标题 */
    fullscreenTitle: {
      type: String,
      default: '表格编辑',
    },
  },
  data() {
    return {
      // handsontable 实例不放进 data，避免被 Vue 响应式代理（会严重拖慢大表格性能）
      // 实例挂在 this.hot 上，见 mounted
      isLoadingData: false,
      // 当前行数的响应式镜像，用于驱动"添加一行"按钮的禁用态
      rowCount: 0,
      // 最近一次校验的错误明细，用于底部错误摘要
      validationErrors: [],

      // ===== 移动端交互状态 =====
      isFullscreen: false,
      // 全屏了但方向锁没生效（仍是竖屏），需要提示用户手动转屏
      needManualRotate: false,
      rowSheetVisible: false,
      // 长按命中的单元格坐标 { row, col }
      activeCell: null,
      // 说明：轻点判定与延时编辑的状态（tapTouchStart / tapEditTimer /
      // tapEditScrollHandler / lastTouchEndTime）与 touchOrigin / longPressTimer
      // 一样，是直接挂在 this 上的非响应式实例字段（触摸高频路径不进 Vue
      // 依赖收集），见 methods。
    };
  },
  computed: {
    /**
     * 容器最终高度（固定高度，不随行数自适应）。
     * 全屏态下返回空，由 CSS 的 flex: 1 撑满剩余空间。
     */
    normalizedHeight() {
      const { height } = this;
      if (height === undefined
        || height === null
        || height === ''
        || height === 0
        || height === 'auto') {
        return '320px';
      }
      if (typeof height === 'number') return `${height}px`;

      const trimmed = String(height).trim();
      if (!trimmed) return '320px';
      // 纯数字字符串按 px 处理，其它带单位的原样使用
      return /^\d+(?:\.\d+)?$/.test(trimmed) ? `${trimmed}px` : trimmed;
    },
    hostStyle() {
      // 全屏态交给 CSS flex 撑满，不下发固定高度
      if (this.isFullscreen) return {};
      return { height: this.normalizedHeight };
    },
    /**
     * stretchH 的最终生效值。
     *
     * 全屏（横屏编辑）态下自动提升为 'all'：屏幕转宽后，若列不多（各列宽之和
     * 小于容器宽），让内核把剩余空间按比例分给各列，表格自然占满横屏区域，
     * 避免右侧留一大块空白。列多时内核的 refreshStretching 里
     * remainingSize <= 0，'all' 不产生任何拉伸，宽表依旧横向滚动，互不干扰。
     *
     * 非全屏态（包括竖屏）保持原值（默认 'none'）：竖屏宽度本来就窄，
     * 拉伸会把少列表格的列压过窄甚至破坏 minColWidth 下限语义。
     *
     * stretchH prop 默认即 'none'，全屏提升只发生在保持默认/未显式配置时；
     * 调用方若明确不想要全屏自动拉伸，传 stretchAutoFullscreen: false。
     */
    effectiveStretchH() {
      if (this.isFullscreen && this.stretchH === 'none' && this.stretchAutoFullscreen !== false) {
        return 'all';
      }
      return this.stretchH;
    },
    /** 触摸优化下被强制关闭的能力集合 */
    touchDisabled() {
      return this.touchOptimized;
    },
    showRowToolbar() {
      return !this.readOnly && this.allowInsertRow && this.showAddRowButton;
    },
    showFooter() {
      return this.showRowToolbar
        || (this.enableLandscape && !this.isFullscreen)
        || !!this.errorSummary
        || !!this.hintText;
    },
    addRowDisabled() {
      return this.rowCount >= this.maxRows;
    },
    /** 底部提示：告诉用户"长按能干什么"，移动端没有右键菜单的发现路径 */
    hintText() {
      if (this.readOnly || !this.longPressRowMenu) return '';
      const actions = [];
      if (this.allowInsertRow) actions.push('插入');
      if (this.allowRemoveRow) actions.push('删除');
      return actions.length ? `长按单元格可${actions.join(' / ')}行` : '';
    },
    errorSummary() {
      if (!this.showErrorSummary) return '';
      return formatValidationMessage(this.validationErrors);
    },
    rowSheetDescription() {
      if (!this.activeCell) return '';
      return `第 ${this.activeCell.row + 1} 行`;
    },
    /** 全屏了但没能转成横屏时，提示用户手动旋转设备 */
    showRotateHint() {
      return this.isFullscreen && this.needManualRotate;
    },
    /** 行操作面板的选项，按 allowInsertRow / allowRemoveRow 动态裁剪 */
    rowSheetActions() {
      if (this.readOnly) return [];
      const actions = [];
      if (this.allowInsertRow) {
        const reachedMax = this.rowCount >= this.maxRows;
        actions.push({ name: '在上方插入一行', key: 'insertAbove', disabled: reachedMax });
        actions.push({ name: '在下方插入一行', key: 'insertBelow', disabled: reachedMax });
        actions.push({
          name: '复制本行到下方',
          key: 'duplicate',
          disabled: reachedMax,
        });
      }
      actions.push({ name: '清空本行内容', key: 'clear' });
      if (this.allowRemoveRow) {
        actions.push({
          name: '删除本行',
          key: 'remove',
          color: '#ee0a24',
          /*
           * 只在「已经没有行」时才禁用。
           *
           * 这里不能拿 minRows 当下限：一旦按 minRows 禁用，最后一行永远删不掉
           * （用户的典型诉求恰恰是"把多出来的那行空行删掉"）。
           * minRows 默认已改为 0，内核也不会再自动补行，因此删空是安全的 ——
           * 删空后底部「添加一行」按钮仍在，用户可以重新开始录入。
           */
          disabled: this.rowCount <= 0,
        });
      }
      return actions;
    },
    resolvedDataSchema() {
      if (this.dataSchema !== undefined) return this.dataSchema;
      return buildDataSchemaFromColumns(this.columns);
    },
    // 归一化后的列定义 + label/value 映射表（对象数组形式的 source 才会产生映射）
    normalizedColumnsResult() {
      return normalizeColumns(this.columns, this.align);
    },
    /**
     * 列宽：显式配了 width 的列用配置值，其余给 minColWidth 下限。
     *
     * 配合 stretchH: 'none'，宽表通过横向滚动浏览 —— 这比把 8 列压进 375px
     * 挤成竖排文字更可用。
     */
    resolvedColWidths() {
      const cols = this.columns;
      if (!Array.isArray(cols) || !cols.length) return undefined;
      const min = this.minColWidth > 0 ? this.minColWidth : MIN_COL_WIDTH;
      return cols.map((col) => {
        const width = col && Number(col.width);
        return width > 0 ? Math.max(width, min) : min;
      });
    },
    hotSettings() {
      const touchOff = this.touchDisabled;

      return {
        data: this.value,
        columns: this.normalizedColumnsResult.columns,
        colWidths: this.resolvedColWidths,
        dataSchema: this.resolvedDataSchema,
        colHeaders: this.colHeaders,
        rowHeaders: this.rowHeaders,
        // 始终给 handsontable 一个确定的高度（跟随 host 容器），它据此启用虚拟滚动
        height: '100%',
        width: '100%',
        // 固定行高：让高度计算确定化。这是 handsontable 原生配置，会被正确纳入
        // 内核的尺寸测量，因此不会像用 CSS 覆盖 td 高度那样破坏对齐与编辑器定位
        rowHeights: this.rowHeight > 0 ? this.rowHeight : MOBILE_ROW_HEIGHT,
        /*
         * 显式关闭 observeChanges。
         *
         * 该插件用 jsonpatch 对数据源做深度脏检查，本组件的数据变更都走 Vue 的
         * value watcher，用不到它。更重要的是：不显式关闭时它的值是 undefined，
         * 而 columnSorting 启用后会在一个 setTimeout 里判定
         * `observeChanges === undefined` 就自动把它打开，进而实例化 DataObserver。
         * 一旦 jsonpatch 因打包问题拿不到，就会抛
         * "Cannot read properties of undefined (reading 'observe')"，
         * 整个 Vue 组件树被卸载（表现为表格点一下就消失）。
         */
        observeChanges: false,
        readOnly: this.readOnly,
        /*
         * minSpareRows / minRows 必须强制转成数字再下发。
         *
         * 表单 schema 常由后端下发或人工编辑，数字项很容易写成字符串。
         * 内核对这两项用的是真值判断（core.js: `if (priv.settings.minSpareRows)`），
         * 字符串 "0" 是 truthy，会让「不自动补空行」的意图彻底失效 ——
         * 表现为用户编辑着就凭空多出一行、删掉最后一行又立刻被补回来。
         */
        minSpareRows: this.safeMinSpareRows,
        minRows: this.safeMinRows,
        minCols: this.minCols,
        maxRows: this.maxRows,
        maxCols: this.maxCols,
        contextMenu: touchOff ? false : this.contextMenu,
        allowInsertRow: this.allowInsertRow,
        allowRemoveRow: this.allowRemoveRow,
        allowInsertColumn: this.allowInsertColumn,
        allowRemoveColumn: this.allowRemoveColumn,
        // 以下都是"必须靠鼠标拖拽"的能力，触摸下会与滚动抢手势，一律强制关闭
        columnFilters: touchOff ? false : this.filters,
        columnSorting: touchOff ? false : this.columnSorting,
        manualColumnResize: touchOff ? false : this.manualColumnResize,
        manualRowResize: touchOff ? false : this.manualRowResize,
        manualColumnMove: touchOff ? false : this.manualColumnMove,
        manualRowMove: touchOff ? false : this.manualRowMove,
        fillHandle: touchOff ? false : this.fillHandle,
        // 冻结首列：宽表横向滚动时保留标识列
        fixedColumnsLeft: this.fixedColumnsLeft > 0
          ? this.fixedColumnsLeft
          : (this.freezeFirstColumn && this.hasEnoughColumnsToFreeze ? 1 : 0),
        fixedRowsTop: this.fixedRowsTop,
        mergeCells: this.mergeCells,
        comments: this.comments,
        // 全屏横屏态下自动按比例拉伸占满宽度，见 effectiveStretchH 注释
        stretchH: this.effectiveStretchH,
        autoWrapRow: true,
        language: 'zh-CN',
        beforeChange: this.handleBeforeChange,
        afterChange: this.handleAfterChange,
        afterCreateRow: this.handleAfterRowsMutated,
        afterRemoveRow: this.handleAfterRowsMutated,
        afterSelectionEnd: this.handleAfterSelectionEnd,
        afterOnCellMouseUp: this.handleAfterOnCellMouseUp,
      };
    },
    /** 只有 3 列以上才值得冻结首列，列少时冻结反而白占宽度 */
    hasEnoughColumnsToFreeze() {
      return Array.isArray(this.columns) && this.columns.length >= 3;
    },
    /**
     * minSpareRows / minRows 的安全值：转成数字，非法或负数一律回落 0。
     * 详见 hotSettings 里下发处的注释。
     */
    safeMinSpareRows() {
      const value = Number(this.minSpareRows);
      return Number.isFinite(value) && value > 0 ? value : 0;
    },
    safeMinRows() {
      const value = Number(this.minRows);
      return Number.isFinite(value) && value > 0 ? value : 0;
    },
    // 结构性配置的指纹：这些项变化时需要调用 updateSettings 让表格重建列结构
    settingsFingerprint() {
      return JSON.stringify({
        columns: this.columns,
        dataSchema: this.dataSchema,
        colHeaders: this.colHeaders,
        rowHeaders: this.rowHeaders,
        mergeCells: this.mergeCells,
        readOnly: this.readOnly,
        fixedColumnsLeft: this.fixedColumnsLeft,
        freezeFirstColumn: this.freezeFirstColumn,
        fixedRowsTop: this.fixedRowsTop,
        contextMenu: this.contextMenu,
        allowInsertRow: this.allowInsertRow,
        allowRemoveRow: this.allowRemoveRow,
        allowInsertColumn: this.allowInsertColumn,
        allowRemoveColumn: this.allowRemoveColumn,
        minSpareRows: this.minSpareRows,
        minRows: this.minRows,
        height: this.height,
        align: this.align,
        rowHeight: this.rowHeight,
        minColWidth: this.minColWidth,
        filters: this.filters,
        columnSorting: this.columnSorting,
        fillHandle: this.fillHandle,
        touchOptimized: this.touchOptimized,
        // effectiveStretchH 随 isFullscreen 变化，进入/退出全屏（横屏）时
        // 指纹变化 -> updateSettings -> 内核重算 stretch，表格自动占满/还原
        stretchH: this.effectiveStretchH,
      });
    },
  },
  watch: {
    /**
     * 外部数据变化时（例如表单异步回填）同步到表格。
     *
     * 这里用"长度 + 内容"判断而非引用比较：Formily 的 field.value 在某些场景下
     * 会保持同一个数组引用做原地修改，只比引用会漏掉回填，表现为"第二次进入页面
     * 数据不显示"。而内部编辑回流的 input 事件里，数据就是 getSourceData() 本身，
     * 引用相同，仍会被第一条判断挡掉，不会产生死循环。
     */
    value(newVal) {
      const hot = this.getHotInstance();
      if (!hot || hot.isDestroyed) return;

      const next = Array.isArray(newVal) ? newVal : [];
      const current = hot.getSourceData();

      // 内部编辑回流：引用相同，无需 loadData
      if (current === next) return;
      // 内容一致也跳过，避免外部无意义的重新赋值导致光标丢失
      if (JSON.stringify(current) === JSON.stringify(next)) return;

      this.isLoadingData = true;
      hot.loadData(next);
      this.isLoadingData = false;
      this.syncRowCount();
      // 回填的数据同样要过一遍校验，历史脏数据才会立刻标红
      this.refreshValidation();
    },
    settingsFingerprint() {
      const hot = this.getHotInstance();
      if (!hot || hot.isDestroyed) return;
      // 只下发结构性配置，data 由上面的 value watcher 单独负责，避免重复 loadData
      const { data, ...rest } = this.hotSettings;
      hot.updateSettings(rest);
      this.syncRowCount();
      this.refreshValidation();
    },
  },
  mounted() {
    this.hot = new Handsontable(this.$refs.hotContainer, this.hotSettings);
    this.syncRowCount();
    this.refreshValidation();

    /*
     * 包装内核的双击进编辑路径。
     *
     * editorManager 把 onCellDblClick 直接映射为 openEditor（EditorManager.init
     * 里 instance.view.wt.update('onCellDblClick', onDblClick)），两条按压路径
     * 都可能触发它：两次快速轻点同一格（内核自己的双击检测），或轻点 + 内核
     * dblClickOrigin 的组合。延时唤起方案下这条旁路必须拦掉，否则"快速点两下"
     * 仍会立即弹输入框。鼠标场景保留原生行为。
     */
    try {
      const wt = this.hot && this.hot.view && this.hot.view.wt;
      if (wt) {
        const originalDblClick = wt.getSetting('onCellDblClick');
        wt.update('onCellDblClick', (event, coords, TD, wtInstance) => {
          // 触摸合成的双击：吞掉，交由轻点 -> 延时唤起链路处理。
          // 注意鼠标双击在这里的事件类型是 mouseup（内核靠 dblClickOrigin
          // 追踪检测双击，不是浏览器的 dblclick 事件），不能按类型误杀。
          if (event && event.type === 'touchend') return;
          // 鼠标双击保留原生行为
          if (typeof originalDblClick === 'function') {
            originalDblClick(event, coords, TD, wtInstance);
          }
        });
      }
    } catch (e) {
      // 包装失败不影响表格可用性，退回原有双击行为
      // eslint-disable-next-line no-console
      console.warn('[HotTable] 包装双击事件失败：', e);
    }

    // 安装横屏 rem 修正（幂等）：修复 amfe-flexible 在横屏时把根字号按
    // clientWidth/10 放大导致的整页拉伸变形。首屏已横屏的场景也会被立即修正。
    installLandscapeRemFix();

    // 屏幕方向 / 视口变化都要重新测量，否则冻结行列的 overlay 会错位
    window.addEventListener('orientationchange', this.handleViewportChange);
    window.addEventListener('resize', this.handleViewportChange);
  },
  beforeDestroy() {
    // 清理待执行的异步任务，避免组件销毁后回调里访问到已 destroy 的实例
    if (this.validateTimer) {
      clearTimeout(this.validateTimer);
      this.validateTimer = null;
    }
    this.cancelPendingTapEdit();
    this.cancelLongPress();
    if (this.dimensionTimer) {
      clearTimeout(this.dimensionTimer);
      this.dimensionTimer = null;
    }

    window.removeEventListener('orientationchange', this.handleViewportChange);
    window.removeEventListener('resize', this.handleViewportChange);

    // 组件在全屏态被销毁（例如路由跳走）时，必须把全屏与方向锁还原，
    // 否则整个页面会残留横屏状态
    if (this.isFullscreen) {
      this.releaseNativeLandscape();
    }

    const hot = this.getHotInstance();
    if (hot && !hot.isDestroyed) {
      hot.destroy();
    }
    this.hot = null;
  },
  methods: {
    getHotInstance() {
      return this.hot;
    },
    syncRowCount() {
      const hot = this.getHotInstance();
      const next = hot && !hot.isDestroyed ? hot.countRows() : 0;
      if (next === this.rowCount) return;

      this.rowCount = next;
    },

    // ==================== 行操作 ====================
    /**
     * 在表格末尾手动追加一行（底部按钮与外部 ref 均可调用）。
     * 与行操作面板的"插入行"走同一条 handsontable alter 通道，因此同样受
     * allowInsertRow / maxRows 约束，并会触发 afterCreateRow 把数据回流给表单。
     */
    addRow(count = 1) {
      if (this.readOnly || !this.allowInsertRow) return;
      const hot = this.getHotInstance();
      if (!hot || hot.isDestroyed) return;
      const total = hot.countRows();
      const amount = Math.min(
        typeof count === 'number' && count > 0 ? count : 1,
        Math.max(this.maxRows - total, 0),
      );
      if (!amount) {
        Toast(`最多 ${this.maxRows} 行`);
        return;
      }
      hot.alter('insert_row', total, amount, 'HotTable.addRow');
      // 定位到新行并滚动过去，避免长表格里"点了没反应"的错觉
      hot.selectCell(total, 0);
    },
    /** 删除指定行（供外部 ref 调用），同样受 allowRemoveRow 约束 */
    removeRow(index, count = 1) {
      if (this.readOnly || !this.allowRemoveRow) return;
      const hot = this.getHotInstance();
      if (!hot || hot.isDestroyed) return;
      hot.alter('remove_row', index, count, 'HotTable.removeRow');
    },
    /** 在指定行前插入一行 */
    insertRowAt(index) {
      const hot = this.getHotInstance();
      if (!hot || hot.isDestroyed) return;
      if (hot.countRows() >= this.maxRows) {
        Toast(`最多 ${this.maxRows} 行`);
        return;
      }
      hot.alter('insert_row', index, 1, 'HotTable.insertRowAt');
      hot.selectCell(index, 0);
    },
    /**
     * 复制某行内容到其下方。
     *
     * 录入场景里相邻行往往只差一两个字段，"复制上一行再改"比逐格重填快得多，
     * 这是桌面端靠拖拽填充手柄完成、移动端必须补上的能力。
     */
    duplicateRow(index) {
      const hot = this.getHotInstance();
      if (!hot || hot.isDestroyed) return;
      if (hot.countRows() >= this.maxRows) {
        Toast(`最多 ${this.maxRows} 行`);
        return;
      }

      // 先按可见列把源行的值取出来，再插入新行。
      // 逐列取值而不是整行对象浅拷贝：对象数组模式下列的 data 可能是 'a.b'
      // 这种嵌套路径，整对象赋值会把兄弟字段一并覆盖掉。
      const colCount = hot.countCols();
      const values = [];
      for (let col = 0; col < colCount; col += 1) {
        values.push(hot.getDataAtCell(index, col));
      }

      hot.alter('insert_row', index + 1, 1, 'HotTable.duplicateRow');

      const changes = values.map((cellValue, col) => [index + 1, col, cellValue]);
      if (changes.length) hot.setDataAtCell(changes, 'HotTable.duplicateRow');
      hot.selectCell(index + 1, 0);
    },
    /** 清空某行所有单元格内容（保留行本身） */
    clearRow(index) {
      const hot = this.getHotInstance();
      if (!hot || hot.isDestroyed) return;
      const colCount = hot.countCols();
      const changes = [];
      for (let col = 0; col < colCount; col += 1) {
        changes.push([index, col, null]);
      }
      if (changes.length) hot.setDataAtCell(changes, 'HotTable.clearRow');
    },

    // ==================== 校验 ====================
    /**
     * 校验整表并返回错误明细。
     *
     * @param {Boolean} [highlight] 是否同时触发 handsontable 全表校验以刷新红框。
     *   默认 true。注意 validateCells() 会引发一次重渲染，若在渲染相关的回调里
     *   调用极易形成「校验 → 重渲染 → 校验」的循环（表现为页面卡死），
     *   所以内部刷新一律传 false，只更新摘要数据。
     * @returns {Array<{ row: Number, column: Number, title: String, message: String }>}
     */
    validate(highlight = true) {
      const hot = this.getHotInstance();
      const data = hot && !hot.isDestroyed ? hot.getSourceData() : this.value;
      const errors = validateTableData(data, this.columns);

      this.validationErrors = errors;

      if (highlight && hot && !hot.isDestroyed) {
        // validateCells 会逐格跑列 validator 并刷新 htInvalid 样式
        hot.validateCells();
      }
      this.$emit('validate', errors);

      return errors;
    },
    /**
     * 内部使用：刷新校验状态（摘要 + 单元格红框）。
     *
     * 必须刷新红框：handsontable 只在「用户写入单元格」时自动校验，
     * 数据由外部回填（loadData）时不会触发，否则历史脏数据永远不标红。
     *
     * 用 setTimeout 合并同一轮里的多次调用（loadData、updateSettings、增删行
     * 往往连续发生），避免 validateCells 被重复触发引起的连环重渲染。
     */
    refreshValidation() {
      const hot = this.getHotInstance();
      if (!hot || hot.isDestroyed) return;

      // 先同步更新摘要，保证 UI 立即反映最新错误
      this.validate(false);

      if (this.validateTimer) clearTimeout(this.validateTimer);
      this.validateTimer = setTimeout(() => {
        this.validateTimer = null;
        const instance = this.getHotInstance();
        if (!instance || instance.isDestroyed) return;
        // 逐格跑列 validator，刷新 htInvalid 样式（红底）
        instance.validateCells();
      }, 0);
    },

    // ==================== handsontable 钩子 ====================
    // 下拉列的 label -> value 转换：用户在下拉里选的是 label，
    // 这里在写入数据源之前换成 value，保证表单最终存的是业务码值。
    // 直接手输/粘贴 value 的情况也兼容（映射里查不到 label 时原样保留）。
    handleBeforeChange(changes, source) {
      if (source === 'loadData') return;
      const { labelValueMaps } = this.normalizedColumnsResult;
      if (!Object.keys(labelValueMaps).length) return;
      const hot = this.getHotInstance();
      if (!hot || hot.isDestroyed) return;
      (changes || []).forEach((change) => {
        if (!change) return;
        const [, prop, , newValue] = change;
        if (newValue === null || newValue === undefined || newValue === '') return;
        // prop 可能是字段名（对象数组模式）或列下标（矩阵模式），统一转成列下标
        const colIndex = typeof prop === 'number' ? prop : hot.propToCol(prop);
        const map = labelValueMaps[colIndex];
        if (!map) return;
        const key = String(newValue);
        if (map.labelToValue.has(key)) {
          // eslint-disable-next-line no-param-reassign
          change[3] = map.labelToValue.get(key);
        }
      });
    },
    handleAfterChange(changes, source) {
      if (source === 'loadData' || this.isLoadingData) return;
      const hot = this.getHotInstance();
      if (!hot || hot.isDestroyed) return;
      const data = hot.getSourceData();
      this.$emit('input', data);
      this.$emit('change', { data, changes, source });
      // 编辑后刷新错误摘要（单元格红框由 handsontable 自身在写值时已更新）
      this.validationErrors = validateTableData(data, this.columns);
      this.$emit('validate', this.validationErrors);
    },
    handleAfterRowsMutated() {
      const hot = this.getHotInstance();
      if (!hot || hot.isDestroyed || this.isLoadingData) return;
      this.syncRowCount();
      this.$emit('input', hot.getSourceData());
      this.refreshValidation();
    },
    handleAfterSelectionEnd(row, column, row2, column2) {
      this.$emit('selection', { row, column, row2, column2 });
    },
    /**
     * 轻点进入编辑（带延时与拖动取消）。
     *
     * handsontable 原生要双击（或按 Enter）才进编辑态，移动端用户没有这个
     * 习惯，表现为"点了半天不能输入"。这里在单元格 mouseup 后打开编辑器。
     *
     * 触摸环境的判定链（内核把 touchstart/touchend 合成为 mousedown/mouseup，
     * 但传给本钩子的 event 仍是原生触摸事件，event.type 为 'touchend'）：
     *   1) host 上 @touchstart 记录了起点（this.tapTouchStart）；
     *   2) @touchmove 位移超过 10px 即判为拖动：清掉起点并取消挂起的编辑；
     *   3) 本钩子里现场做轻点判定：event.type === 'touchend' 时要求
     *      位移 <= 10px 且按压 <= 500ms，否则直接返回 —— 滚动、长按、
     *      多指都不会误开输入框（时序说明：内核在 .wtHolder 上的 touchend
     *      监听先于 host 上的 Vue 监听触发，因此必须在钩子内现场判定，
     *      不能依赖 handleTouchEnd 先行写入的标记）；
     *   4) 判定通过后再延迟 tapEditDelay（默认 1000ms）才真正唤起输入框。
     *      延时窗口内新增 touchstart / touchmove 超阈值 / 页面或表格滚动
     *      都会取消。鼠标点击不延时（保持点开即编辑）。
     *
     * 其它必须绕开的坑：
     *   1) 长按刚弹出行操作面板时，浏览器仍会补发 mouseup，会把编辑器一起
     *      打开，导致面板背后有个输入框在抢焦点 —— 用 longPressFired 标记挡掉；
     *   2) 点表头 / 行号（row < 0 或 col < 0）不该进编辑；
     *   3) iPad 桌面版 UA 等场景下内核会同时注册触摸与鼠标监听，touchend
     *      后浏览器补发的合成 mouseup 会以"鼠标点击"身份绕过延时 —— 用
     *      lastTouchEndTime（600ms 内的 mouseup 视为合成事件）挡掉。
     */
    handleAfterOnCellMouseUp(event, coords) {
      if (!this.tapToEdit || this.readOnly) return;
      if (this.longPressFired) return;
      if (!coords || coords.row < 0 || coords.col < 0) return;

      // 触摸事件需要通过轻点判定；鼠标点击（调试 / 外接鼠标）不受影响
      if (event && event.type === 'touchend') {
        // 先记录触摸结束时刻（无论本次是否轻点）：之后 600ms 内的 mouseup
        // 一律视为浏览器补发的合成事件，防止滚动等"判定失败"的触摸结束后，
        // 合成 mouseup 以鼠标点击身份绕过延时直接打开编辑器
        this.lastTouchEndTime = Date.now();

        const start = this.tapTouchStart;
        if (!start) return;
        const endTouch = event.changedTouches && event.changedTouches[0];
        if (!endTouch) return;
        const moved = Math.abs(endTouch.clientX - start.x)
          + Math.abs(endTouch.clientY - start.y);
        const duration = Date.now() - start.time;
        if (moved > TAP_MOVE_TOLERANCE || duration > TAP_MAX_DURATION_MS) return;
        // 消费掉起点：本次触摸周期结束，防止双击第二下被误判
        this.tapTouchStart = null;
      } else if (event && (event.type === 'mouseup' || event.type === 'click')) {
        // 距离最近一次触摸结束太近的鼠标事件视为合成事件，忽略
        if (Date.now() - (this.lastTouchEndTime || 0) < SYNTHETIC_MOUSE_EVENT_WINDOW_MS) return;
      }

      const hot = this.getHotInstance();
      if (!hot || hot.isDestroyed) return;

      // 只读单元格不进编辑，避免弹出一个改不了的输入框
      const cellMeta = hot.getCellMeta(coords.row, coords.col);
      if (cellMeta && cellMeta.readOnly) return;

      const delay = this.resolvedTapEditDelay();
      this.cancelPendingTapEdit();

      const openEditor = () => {
        this.tapEditTimer = null;
        this.detachTapEditScrollGuard();
        const instance = this.getHotInstance();
        if (!instance || instance.isDestroyed) return;
        // 打开前再校验一次选区没被移动走（延时期间用户可能点了别的单元格）
        const selected = instance.getSelectedLast();
        if (!selected || selected[0] !== coords.row || selected[1] !== coords.col) return;
        const editor = instance.getActiveEditor();
        if (editor && !editor.isOpened()) {
          editor.beginEditing();
        }
      };

      // 鼠标点击（调试 / 外接鼠标）不做延时，保持"点开即编辑"；
      // 延时窗口只服务于触摸场景的"先拖后点"诉求
      const isTouchEvent = !!(event && event.type === 'touchend');
      if (delay <= 0 || !isTouchEvent) {
        // 立即唤起：沿用旧版"下一个宏任务"的做法，避开内核选区提交时序
        setTimeout(openEditor, 0);
        return;
      }

      // 延时唤起：窗口内任何新的触摸起点 / 拖动 / 惯性滚动都会取消
      this.tapEditTimer = setTimeout(openEditor, delay);
      this.attachTapEditScrollGuard();
    },

    // ==================== 轻点判定与延时编辑 ====================
    /**
     * 解析 tapEditDelay prop：schema 里可能配成字符串数字。
     * 非法值（负数 / NaN）回落默认 1000ms。
     */
    resolvedTapEditDelay() {
      const raw = typeof this.tapEditDelay === 'string'
        ? Number(this.tapEditDelay.trim())
        : this.tapEditDelay;
      if (!Number.isFinite(raw) || raw < 0) return TAP_EDIT_DELAY_MS;
      return raw;
    },
    /** 取消挂起的延时编辑（拖动表格 / 组件销毁时调用） */
    cancelPendingTapEdit() {
      if (this.tapEditTimer) {
        clearTimeout(this.tapEditTimer);
        this.tapEditTimer = null;
      }
      this.detachTapEditScrollGuard();
    },
    /**
     * 延时唤起窗口的滚动守卫：手指抬起后表格仍会惯性滚动几百毫秒，
     * 期间表格容器或外层页面一旦发生滚动，就说明这次"轻点"其实是滚动
     * 操作，立即取消挂起的编辑。
     * 监听 window 的捕获阶段 scroll：既能吃到 wtHolder 内部滚动（冒泡到
     * window 之前先被捕获截获），也能吃到外层页面滚动。
     */
    attachTapEditScrollGuard() {
      if (this.tapEditScrollHandler) return;
      this.tapEditScrollHandler = () => this.cancelPendingTapEdit();
      window.addEventListener('scroll', this.tapEditScrollHandler, {
        capture: true,
        passive: true,
      });
    },
    detachTapEditScrollGuard() {
      if (!this.tapEditScrollHandler) return;
      window.removeEventListener('scroll', this.tapEditScrollHandler, { capture: true });
      this.tapEditScrollHandler = null;
    },

    // ==================== 长按行操作 ====================
    handleTouchStart(event) {
      this.longPressFired = false;
      // 记录轻点判定起点（afterOnCellMouseUp 钩子里现场判定是否轻点）。
      // 多指（缩放 / 第二根手指落下）不参与轻点与长按判定。
      if (event.touches && event.touches.length === 1) {
        const touch = event.touches[0];
        this.tapTouchStart = { x: touch.clientX, y: touch.clientY, time: Date.now() };
      } else {
        this.tapTouchStart = null;
      }
      // 新触摸落下时，此前挂起的延时编辑一律作废（用户开始新的操作）
      this.cancelPendingTapEdit();

      if (!this.longPressRowMenu || this.readOnly) return;
      // 多指操作（缩放等）不参与长按判定
      if (!event.touches || event.touches.length !== 1) return;

      const touch = event.touches[0];
      this.touchOrigin = { x: touch.clientX, y: touch.clientY };
      this.cancelLongPress();
      this.longPressTimer = setTimeout(() => {
        this.longPressTimer = null;
        this.triggerLongPress(event);
      }, LONG_PRESS_MS);
    },
    handleTouchMove(event) {
      if (!event.touches || !event.touches.length) return;
      const touch = event.touches[0];
      const start = this.tapTouchStart;
      // 拖动表格超过阈值：无论后续是否抬手，都不是轻点，取消挂起的编辑
      if (start) {
        const tapMoved = Math.abs(touch.clientX - start.x) + Math.abs(touch.clientY - start.y);
        if (tapMoved > TAP_MOVE_TOLERANCE) {
          this.tapTouchStart = null;
          this.cancelPendingTapEdit();
        }
      }

      if (!this.longPressTimer || !this.touchOrigin) return;
      const moved = Math.abs(touch.clientX - this.touchOrigin.x)
        + Math.abs(touch.clientY - this.touchOrigin.y);
      // 手指明显移动说明用户在滚动表格，撤销长按
      if (moved > LONG_PRESS_MOVE_TOLERANCE) this.cancelLongPress();
    },
    handleTouchEnd(event) {
      this.cancelLongPress();
      // 长按面板已弹出的情况下，本轮触摸不再进编辑（longPressFired 会在
      // 钩子里挡一道），这里再把挂起的延时编辑一并取消
      if (this.longPressFired) {
        this.cancelPendingTapEdit();
      }
      // touchcancel（滚动劫持 / 系统手势打断）：内核只监听 touchend，不会
      // 走 mouseup 模拟，钩子不会触发，这里直接作废轻点起点
      if (event && event.type === 'touchcancel') {
        this.tapTouchStart = null;
        this.cancelPendingTapEdit();
      }
      // 注意：touchend 时 tapTouchStart 不在这里清空 —— 内核在 .wtHolder
      // 上的 touchend 监听先于本监听触发，afterOnCellMouseUp 钩子需要起点
      // 做轻点判定；判定通过后钩子内部会自行消费（置 null）。
    },
    cancelLongPress() {
      if (this.longPressTimer) {
        clearTimeout(this.longPressTimer);
        this.longPressTimer = null;
      }
    },
    /**
     * 长按达成，定位被按住的单元格并弹出行操作面板。
     *
     * 定位优先用 handsontable 的当前选区：touchstart 会被内核转成 mousedown 并
     * 立即更新选区，长按触发时选区已经落在手指按住的那一格，比自己解析 DOM 稳。
     * 冻结列/冻结行的单元格属于 overlay 里的克隆表格，getCoords 拿不到坐标，
     * 因此把 DOM 解析作为兜底而非主路径。
     */
    triggerLongPress(event) {
      const hot = this.getHotInstance();
      if (!hot || hot.isDestroyed) return;
      if (!this.rowSheetActions.length) return;

      let coords = null;
      const selected = hot.getSelectedLast();
      if (selected && selected[0] >= 0) {
        coords = { row: selected[0], col: Math.max(selected[1], 0) };
      } else {
        // 兜底：从事件目标反查 td
        try {
          const td = event.target && event.target.closest && event.target.closest('td');
          if (td) {
            const parsed = hot.getCoords(td);
            if (parsed && parsed.row >= 0) coords = { row: parsed.row, col: parsed.col };
          }
        } catch (e) {
          // 克隆表格里的 td 拿不到坐标，放弃本次长按
        }
      }
      if (!coords) return;

      this.longPressFired = true;
      this.activeCell = coords;
      this.rowSheetVisible = true;
    },
    handleRowAction(action) {
      this.rowSheetVisible = false;
      if (!action || action.disabled || !this.activeCell) return;
      const { row } = this.activeCell;

      switch (action.key) {
        case 'insertAbove':
          this.insertRowAt(row);
          break;
        case 'insertBelow':
          this.insertRowAt(row + 1);
          break;
        case 'duplicate':
          this.duplicateRow(row);
          break;
        case 'clear':
          this.clearRow(row);
          break;
        case 'remove':
          this.removeRow(row, 1);
          break;
        default:
          break;
      }
      this.activeCell = null;
    },

    // ==================== 横屏 / 全屏 ====================
    /**
     * 进入全屏，并尽量把设备转成横屏。
     *
     * ⚠️ 这里刻意**不做** CSS rotate(90deg) 强制横屏。
     * 曾经用过，真机（iOS 企微 WebView）实测有两处无法回避的错乱：
     *   1) 系统输入法键盘永远按设备真实方向弹出 —— 表格被转了 90°，键盘还是竖的，
     *      候选词条与输入框完全错位，没法正常打字；
     *   2) 日期编辑器（pikaday）由内核挂到 document.body 上
     *      （见 vendor/handsontable/editors/dateEditor.js 的 `document.body.appendChild`），
     *      不在旋转容器内，方向与展开位置都是错的。
     * 这两类都是"原生/组件外部节点不跟随 transform"导致的，CSS 层面无解。
     *
     * 因此降级策略改为两级：
     *   1) requestFullscreen + screen.orientation.lock('landscape')
     *      —— Android / 多数 X5 支持，系统级真旋转，键盘与日期选择器都正常；
     *   2) 锁定不可用（iOS WebView）：只进全屏，并提示用户手动转屏。
     *      用户手动转屏后设备真实方向就是横屏，一切原生控件方向自然正确。
     */
    async enterFullscreen() {
      this.isFullscreen = true;
      await this.$nextTick();

      const locked = await this.tryNativeLandscape();
      // 锁定失败且当前仍是竖屏 → 提示用户自己转；已是横屏则无需提示
      this.needManualRotate = !locked && this.isPortrait();

      this.scheduleDimensionRefresh();
      this.$emit('fullscreen-change', true);
    },
    async exitFullscreen() {
      this.releaseNativeLandscape();
      this.needManualRotate = false;
      this.isFullscreen = false;
      await this.$nextTick();

      this.scheduleDimensionRefresh();
      this.$emit('fullscreen-change', false);
    },
    /** 尝试原生全屏 + 方向锁，真的转成横屏才返回 true */
    async tryNativeLandscape() {
      const el = this.$el;
      try {
        const request = el.requestFullscreen
          || el.webkitRequestFullscreen
          || el.webkitRequestFullScreen;
        // 方向锁在多数实现里要求文档处于全屏态，因此先申请全屏
        if (request && !document.fullscreenElement && !document.webkitFullscreenElement) {
          await request.call(el);
        }
      } catch (e) {
        // iOS WebView 对非 video 元素不支持全屏；失败不影响后续提示引导
      }

      try {
        const orientation = window.screen && window.screen.orientation;
        if (orientation && typeof orientation.lock === 'function') {
          await orientation.lock('landscape');
          /*
           * 不能只看 lock() 有没有抛错。
           *
           * X5 的部分版本里 lock() 既不抛异常、也不返回 Promise，更不真的转屏 ——
           * 只看返回值会误判成功，于是不再提示用户手动转屏，人就卡在竖屏全屏里。
           * 因此等一小会儿后回读真实方向，用结果说话。
           */
          await new Promise((resolve) => setTimeout(resolve, 150));
          return !this.isPortrait();
        }
      } catch (e) {
        // iOS 无该 API、X5 部分版本抛 NotSupportedError / SecurityError
      }
      return false;
    },
    releaseNativeLandscape() {
      try {
        const orientation = window.screen && window.screen.orientation;
        if (orientation && typeof orientation.unlock === 'function') {
          orientation.unlock();
        }
      } catch (e) {
        // 未锁定时 unlock 可能抛错，忽略
      }
      try {
        const exit = document.exitFullscreen || document.webkitExitFullscreen;
        if (exit && (document.fullscreenElement || document.webkitFullscreenElement)) {
          exit.call(document);
        }
      } catch (e) {
        // 已退出全屏时重复调用会抛错，忽略
      }
    },
    isPortrait() {
      if (window.matchMedia) {
        return window.matchMedia('(orientation: portrait)').matches;
      }
      return window.innerHeight >= window.innerWidth;
    },
    /**
     * 视口/方向变化处理：用户自己转成横屏后撤掉提示，并重新测量表格尺寸。
     */
    handleViewportChange() {
      if (this.needManualRotate && !this.isPortrait()) {
        this.needManualRotate = false;
      }
      // 视口/方向变化伴随表格重排，挂起的延时编辑一律取消
      this.cancelPendingTapEdit();
      this.scheduleDimensionRefresh();
    },
    /**
     * 延迟重新测量表格尺寸。
     *
     * 方向切换、全屏进出都伴随浏览器的布局动画，立即测量会拿到中间态尺寸，
     * 因此做两次：下一帧一次（响应快），300ms 后再一次（保证最终态正确）。
     */
    scheduleDimensionRefresh() {
      this.refreshDimensions();
      if (this.dimensionTimer) clearTimeout(this.dimensionTimer);
      this.dimensionTimer = setTimeout(() => {
        this.dimensionTimer = null;
        this.refreshDimensions();
      }, 300);
    },
    /**
     * 重新测量并重绘表格。
     *
     * 注意 v6.2.2 内核没有 v7+ 的 refreshDimensions() API，只能手动组合：
     * 先更新可滚动容器引用，再整表重绘，最后刷新所有 overlay（冻结行列）的位置。
     * 少了最后一步，横屏后冻结列会停在原来的位置上不动。
     */
    refreshDimensions() {
      const hot = this.getHotInstance();
      if (!hot || hot.isDestroyed) return;
      try {
        const overlays = hot.view && hot.view.wt && hot.view.wt.wtOverlays;
        if (overlays && typeof overlays.updateMainScrollableElements === 'function') {
          overlays.updateMainScrollableElements();
        }
        hot.render();
        if (overlays && typeof overlays.refreshAll === 'function') {
          overlays.refreshAll();
        }
      } catch (e) {
        // 尺寸刷新是尽力而为的补偿动作，失败不应影响表格可用性
        // eslint-disable-next-line no-console
        console.warn('[HotTable] 刷新表格尺寸失败：', e);
      }
    },

    // ==================== 对外 API ====================
    // 供外部（父组件 / Formily scope）主动调用：重新渲染表格
    render() {
      const hot = this.getHotInstance();
      if (hot && !hot.isDestroyed) hot.render();
    },
    // 导出当前表格的纯数据（不含 handsontable 内部状态）
    exportData() {
      const hot = this.getHotInstance();
      return hot && !hot.isDestroyed ? hot.getSourceData() : this.value;
    },
    // 供外部按列下标做 value -> label 反查（例如导出报表时需要显示文本）
    getOptionLabel(colIndex, value) {
      const map = this.normalizedColumnsResult.labelValueMaps[colIndex];
      if (!map) return value;
      const key = value === null || value === undefined ? '' : String(value);
      return map.valueToLabel.has(key) ? map.valueToLabel.get(key) : value;
    },
  },
};
</script>

<style scoped>
/* ===== 设计变量（TDesign 色板，与 if-web 保持一致） ===== */
.hot-table-mobile {
  --ht-brand: #0052d9;
  --ht-brand-active: #003cab;
  --ht-text: rgba(0, 0, 0, 0.9);
  --ht-text-secondary: rgba(0, 0, 0, 0.6);
  --ht-text-disabled: rgba(0, 0, 0, 0.26);
  --ht-border: #dcdcdc;
  --ht-error: #d54941;

  width: 100%;
  display: flex;
  flex-direction: column;
  font-size: 14px;

  /*
    建立独立的层叠上下文（stacking context）。

    handsontable 的冻结表头/冻结列是绝对定位的 overlay，内核给了很高的
    z-index（.ht_clone_top 101、.ht_clone_left 102、角落 103、编辑器 104、
    还有 199 的），这些值会盖过页面顶部导航栏与底部提交条，
    表现为滚动时表头/单元格浮在导航栏之上。

    position + z-index 让这些 overlay 的层级只在本组件内部生效，
    对外整体只占 z-index: 0 这一层。
  */
  position: relative;
  z-index: 0;
  isolation: isolate;
}

.htm-host {
  width: 100%;
  /* 非全屏态高度由 hostStyle 内联下发；这里不能用 flex: 1，
     否则会被工具条挤掉一两行（就是"总是少一行"的成因） */
  flex: none;
  overflow: hidden;
  border-radius: 3px;
}

/* ---------- 全屏态 ---------- */
.hot-table-mobile.is-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2000;
  background: #fff;
  /* 刘海屏/圆角屏安全区，避免顶栏按钮被系统 UI 盖住 */
  padding: env(safe-area-inset-top) env(safe-area-inset-right)
    env(safe-area-inset-bottom) env(safe-area-inset-left);
  box-sizing: border-box;
}

/* 全屏态下表格撑满剩余空间：min-height: 0 是必须的，
   否则 flex 子项会以内容高度为下限，把工具条顶出屏幕 */
.hot-table-mobile.is-fullscreen .htm-host {
  flex: 1;
  min-height: 0;
  height: auto !important;
}

/*
  这里刻意没有「CSS rotate 强制横屏」的样式。
  原因见 enterFullscreen 的注释：系统键盘与挂在 body 上的日期选择器
  不会跟随 transform 旋转，会导致输入与选日期完全错乱。
  方向锁不可用时改为提示用户手动转屏。
*/

.htm-rotate-hint {
  flex: none;
  padding: 8px 12px;
  font-size: 13px;
  line-height: 18px;
  color: #ad6800;
  background: #fffbe6;
  border-bottom: 1px solid #ffe58f;
}

.htm-topbar {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 44px;
  padding: 0 12px;
  border-bottom: 1px solid #e7e7e7;
}

.htm-topbar-title {
  font-size: 16px;
  font-weight: 500;
  color: var(--ht-text);
}

.htm-topbar-btn {
  height: 32px;
  padding: 0 12px;
  font-size: 14px;
  color: var(--ht-brand);
  background: transparent;
  border: none;
  outline: none;
}

.htm-toolbar {
  flex: none;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  padding: 8px 0 0;
}

/* 不依赖 Vant Button，避免它的全局样式（如 line-height）影响紧凑布局 */
.htm-btn {
  height: 32px;
  padding: 0 14px;
  margin-right: 8px;
  font-size: 14px;
  color: var(--ht-text);
  background: #fff;
  border: 1px solid var(--ht-border);
  border-radius: 4px;
  outline: none;
  /* 去掉移动端点击时的灰色高亮块 */
  -webkit-tap-highlight-color: transparent;
}

.htm-btn-primary {
  color: var(--ht-brand);
  border-color: var(--ht-brand);
}

.htm-btn-primary:active:not(:disabled) {
  color: #fff;
  background: var(--ht-brand-active);
  border-color: var(--ht-brand-active);
}

.htm-btn:disabled {
  color: var(--ht-text-disabled);
  background: #f3f3f3;
  border-color: var(--ht-border);
}

.htm-tip {
  font-size: 12px;
  color: var(--ht-text-secondary);
}

.htm-error-tip {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  line-height: 18px;
  color: var(--ht-error);
  /* 摘要可能很长，移动端最多显示两行，其余截断（完整内容在 title 里） */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>

<!--
  皮肤与移动端触摸细节覆盖。

  表格主体由 handsontable 直接操作 DOM 渲染，节点不带 Vue 的 scoped 属性，
  因此这里不能加 scoped；统一用 .hot-table-mobile 前缀把影响范围限制在本组件内，
  不会污染页面上其它 handsontable 实例。

  ⚠️ 只改颜色与触摸行为，绝不覆盖 height / line-height / padding / box-sizing
  这些参与尺寸计算的属性 —— handsontable 的行高列宽是 JS 实测 DOM 后写入内联
  样式的，虚拟滚动、冻结行列对齐、下拉编辑器定位全依赖这套测量结果。
  需要调整行高请改 rowHeight prop（走原生 rowHeights 配置）。
-->
<style>
/* ---------- 触摸滚动 ---------- */
/*
  overscroll-behavior: contain 阻止滚动链：表格纵向滚到底后，
  继续上滑不会把外层页面一起带走（企微里还会误触发下拉刷新）。
*/
.hot-table-mobile .wtHolder {
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

/* 长按由本组件接管（弹行操作面板），屏蔽系统的文本选中与"复制/查询"菜单 */
.hot-table-mobile .handsontable td,
.hot-table-mobile .handsontable th {
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

/* 编辑态的输入框必须允许选中，否则无法调整光标位置 */
.hot-table-mobile .handsontableInput {
  -webkit-user-select: text;
  user-select: text;
}

/* ---------- 边框与文字 ---------- */
.hot-table-mobile .handsontable th,
.hot-table-mobile .handsontable td {
  border-right-color: #e7e7e7;
  border-bottom-color: #e7e7e7;
  color: rgba(0, 0, 0, 0.9);
}

/* ---------- 表头 ---------- */
.hot-table-mobile .handsontable th {
  background-color: #f3f3f3;
  color: rgba(0, 0, 0, 0.9);
}

/* 行号列：弱化为次要信息。
   只改颜色，不动对齐方式（内核用 text-align: center 保证与数据行基线一致） */
.hot-table-mobile .handsontable tbody tr th {
  background-color: #fafafa;
  color: rgba(0, 0, 0, 0.6);
}

.hot-table-mobile .handsontable th.ht__highlight {
  background-color: #eaeaea;
}

/* ---------- 选中态 ---------- */
.hot-table-mobile .handsontable .wtBorder.current,
.hot-table-mobile .handsontable .wtBorder.area {
  background-color: #0052d9 !important;
}

.hot-table-mobile .handsontable td.area,
.hot-table-mobile .handsontable td.area-1,
.hot-table-mobile .handsontable td.area-2,
.hot-table-mobile .handsontable td.area-3,
.hot-table-mobile .handsontable td.area-4,
.hot-table-mobile .handsontable td.area-5,
.hot-table-mobile .handsontable td.area-6,
.hot-table-mobile .handsontable td.area-7 {
  background-color: #f2f3ff;
}

/* ---------- 校验失败 ---------- */
/* 原生是整格铺满高饱和红，过于刺眼；改为浅红底 + 红字，与 TDesign 错误态一致 */
.hot-table-mobile .handsontable td.htInvalid {
  background-color: #fff0ed !important;
  color: #d54941 !important;
}

/* ---------- 只读 ---------- */
.hot-table-mobile .handsontable td.htDimmed {
  color: rgba(0, 0, 0, 0.4);
  background-color: #fafafa;
}

/* ---------- 下拉箭头 ---------- */
/*
  只改颜色不改尺寸：箭头的点击热区由内核按 td 高度计算，
  行高已经提到 40px，热区随之变大，无需再动 CSS。
*/
.hot-table-mobile .handsontable .htAutocompleteArrow {
  color: rgba(0, 0, 0, 0.55);
}

/* ---------- 复选框列 ---------- */
.hot-table-mobile .handsontable input[type='checkbox'] {
  accent-color: #0052d9;
}

/* 编辑态输入框：聚焦描边用品牌蓝 */
.hot-table-mobile .handsontableInput {
  color: rgba(0, 0, 0, 0.9);
  box-shadow: inset 0 0 0 1px #0052d9;
}

/*
  下拉编辑器（listbox）挂在编辑器容器内，因此能被本组件前缀命中。
  选中项用品牌蓝浅底，替换原生的灰绿色。
*/
.hot-table-mobile .handsontable.listbox td.current,
.hot-table-mobile .handsontable.listbox td:hover {
  background-color: #f2f3ff;
}

/* 必填星号 */
.hot-table-mobile .handsontable th .ht-required-mark {
  margin-left: 2px;
  color: #d54941;
}
</style>
